import { produce } from 'immer';
import { StateCreator } from 'zustand';

import { JobStatus } from '../service/workflow/workflow.type';

export interface Job {
  id: number;
  status: JobStatus;
  output: {
    output_action: string;
    output_id: number;
    is_ready: boolean;
    is_blocked: boolean;
    [key: string]: unknown;
  };
  updated_at: string;
  is_simple?: boolean;
  [key: string]: unknown;
}

/**
 * This is just the state of the workflow slice.
 */
export interface WorkflowState {
  jobs: Job[];
  isWorkflowTask: boolean;
}

/**
 * Workflow-related actions.
 * If you want to add more, define them here.
 */
export interface WorkflowActions {
  setJobs: (jobs: Job[]) => void;
  appendJob: (job: Job) => void;
  isActionPending: (action: string) => boolean;
  isActionReady: (action: string) => boolean;
  isSimpleTask: (action: string) => boolean;
  updateLatestJobByAction: (action: string, job: Job) => void;
  setIsWorkflowTask: (isWorkflowTask: boolean) => void;
}

export type WorkflowSlice = {
  workflow: WorkflowState & WorkflowActions;
};

/**
 * Creates the workflow slice. This function can be merged into
 * a larger store (e.g. a workspace store).
 */
export const createWorkflowSlice: StateCreator<WorkflowSlice, [], [], WorkflowSlice> = (
  set,
  get
) => ({
  workflow: {
    jobs: [],
    isWorkflowTask: false,

    setJobs: (jobs: Job[]) => {
      set(
        produce((draft: WorkflowSlice) => {
          draft.workflow.jobs = jobs;
        })
      );
    },

    appendJob: (job: Job) =>
      set(
        produce((draft: WorkflowSlice) => {
          draft.workflow.jobs.push(job);
        })
      ),

    isActionPending: (action: string): boolean => {
      const { workflow } = get();

      return workflow.jobs.some(
        (job) =>
          job.output.output_action === action && !job.output.is_ready && job.status !== 'closed'
      );
    },

    isActionReady: (action: string): boolean => {
      const { workflow } = get();

      return workflow.jobs.some(
        (job) =>
          job.output.output_action === action && job.output.is_ready && job.status !== 'closed'
      );
    },

    updateLatestJobByAction: (action, job) => {
      const { workflow } = get();
      const index = workflow.jobs.findIndex((job) => job.output.output_action === action);

      if (index === -1) return;

      set(
        produce((draft: WorkflowSlice) => {
          draft.workflow.jobs[index] = job;
        })
      );
    },

    setIsWorkflowTask: (isWorkflowTask: boolean) =>
      set(
        produce((draft: WorkflowSlice) => {
          draft.workflow.isWorkflowTask = isWorkflowTask;
        })
      ),

    isSimpleTask: (action: string): boolean => {
      const { workflow } = get();

      return workflow.jobs.some((job) => job.output.output_action === action && job.is_simple);
    },
  },
});
